import React, { useEffect, useState, useRef } from 'react';
import './MyStories.css'; // Import styles
import { getMyStories, deleteStory, getStoryGenerationStatus, getStoryCoverById, toggleStoryPublicStatus, getStoryFeedbackSummary, getStoryFeedbacks, getStoryPDFDownloadLink } from '../../api/storyApi';
import FeedbackModal from './FeedbackModal';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StoryShareModal from './StoryShareModal';
import QuestionModal from '../common/QuestionModal';
import Modal from '../common/Modal';
import { formatDistanceToNow, isoDateDifference } from '../../utils/dateUtils';
import { useSectionVisibility } from '../../hooks/useSectionVisibility';
import LoadingOverlay from '../common/LoadingOverlay';

const MyStories = ({ newStoriesToAdd, onLoaded, onVisible }) => {
    const [stories, setStories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [selectedStory, setSelectedStory] = useState(null);
    const [feedbackData, setFeedbackData] = useState([]);
    const [selectedStoryId, setSelectedStoryId] = useState(null);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [totalFeedbacks, setTotalFeedbacks] = useState(0);
    const [feedbackPage, setFeedbackPage] = useState(1);
    const [showShareModal, setShowShareModal] = useState(false);
    const [selectedStoryToShare, setSelectedStoryToShare] = useState(null);
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
    const [modalMessage, setModalMessage] = useState(null);
    const ref = useSectionVisibility('my-stories', onVisible);

    const navigate = useNavigate();
    const { t } = useTranslation();
    const firstLoad = useRef(true);

    useEffect(() => {

        const pollStoryStatus = async (storyId) => {
            const interval = setInterval(async () => {
                try {
                    const { generatingStatus } = await getStoryGenerationStatus(storyId);
                    setStories((prevStories) =>
                        prevStories.map((story) =>
                            story._id === storyId ? { ...story, generatingStatus } : story
                        )
                    );
    
                    if (generatingStatus === 'CompletelyGenerated' || generatingStatus === 'PartiallyGenerated') {
                        clearInterval(interval);
                        const storyCover = await getStoryCoverById(storyId);
                        setStories((prevStories) => prevStories.map((story) => story._id === storyId ? storyCover : story));
                    }

                } catch (error) {
                    // console.error(`Error polling status for story ${storyId}:`, error);
                    if (error.response && error.response.status === 404) {
                        setModalMessage('myStories.errorCreatingStory');
                        setStories((prevStories) => prevStories.filter((story) => story._id !== storyId));
                    }
                    clearInterval(interval);
                }
            }, 5000); // Poll every 5 seconds
        };    

        const fetchStories = async (page) => {
            setError(null);
            setLoading(true);
            try {
                const response = await getMyStories(page);
                setStories(response.stories);
                setTotalPages(response.totalPages);
    
                // Start polling for stories still generating
                response.stories
                    .filter((story) => (((story.generatingStatus !== 'CompletelyGenerated') && (story.generatingStatus !== 'PartiallyGenerated') && (isoDateDifference(story.date) < 1))))
                    .forEach((story) => pollStoryStatus(story._id));

                // Fetch feedback summary
                response.stories.forEach((story) => fetchFeedbackSummary(story._id));
            } catch (error) {
                setError('myStories.errorFetchingStories');
            } finally {
                setLoading(false);
                if (firstLoad.current) {
                    onLoaded(2); // sectionId for MyStories = 2
                    firstLoad.current = false;
                }
            }
        };
    
        if (newStoriesToAdd.length > 0) {
            setPage(1);
            firstLoad.current = true;
        }
        fetchStories(page);
    }, [page, newStoriesToAdd, onLoaded]);

    useEffect(() => {
        if (modalMessage) {
            const timer = setTimeout(() => setModalMessage(null), 10000); // 10 seconds
            return () => clearTimeout(timer);
        }
    }, [modalMessage]);

    const handleNextPage = () => {
        setPage(page + 1);
    };
    
    const handlePrevPage = () => {
        if (page > 1) {
            setPage(page - 1);
        }
    };

    const handleStoryClick = (story) => {
        setSelectedStory(story); // Set the selected story for overlay display
    };

    const handleCloseOverlay = () => {
        setSelectedStory(null); // Close the overlay by clearing the selected story
    };

    const handlePlayStory = (storySlug) => {
        navigate(`/story/${storySlug}`);
    }

    const handleMakePublic = async (storyId) => {
        await toggleStoryPublicStatus(storyId);
        setModalMessage('myStories.storyPublished');
        setStories((prevStories) => prevStories.map((story) => story._id === storyId ? { ...story, isPublic: true } : story))
        setSelectedStory(null);
    }

    const handleDeleteStory = async (storyId) => {
        setShowDeleteConfirmationModal(true);
    }

    const confirmDelete =  async () => {
        try {
            await deleteStory(selectedStory._id);
            setStories(stories.filter(story => story._id !== selectedStory._id));
          } catch(error) {
            console.error(t('errorDeletingStory'));
          } finally {
              handleCloseOverlay();
          }
        setShowDeleteConfirmationModal(false);
    };

    const fetchFeedbackSummary = async (storyId) => {
        try {
            const summary = await getStoryFeedbackSummary(storyId);
            setStories((prevStories) =>
                prevStories.map((story) =>
                    story._id === storyId ? { ...story, ...summary } : story
                )
            );
        } catch (error) {
            console.error("Error fetching feedback summary:", error);
        }
    };

    const fetchFeedbacks = async (storyId, feedbackPage = 1) => {
        try {
            const { feedbacks, totalFeedbacks } = await getStoryFeedbacks(storyId, feedbackPage);
            setFeedbackData(feedbacks);
            setTotalFeedbacks(totalFeedbacks);
            setFeedbackPage(feedbackPage);
        } catch (error) {
            console.error("Error fetching feedbacks:", error);
        }
    };

    const openFeedbackModal = (storyId) => {
        setSelectedStoryId(storyId);
        setShowFeedbackModal(true);
        fetchFeedbacks(storyId);
    };

    const openShareModal = (story) => {
        setSelectedStoryToShare(story);
        setShowShareModal(true);
        handleCloseOverlay();
    };

    const handleDownload = async (storyId) => {
        setError(null);
        setLoading(true);
        try {
            const pdfUrlRequest = await getStoryPDFDownloadLink(storyId);
            const url = pdfUrlRequest.signedUrl;

            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = '';
            anchor.target = '_blank';
            anchor.style.display = 'none';
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
        } catch (error) {
            if (error.response?.status === 404 && error.response?.data?.message === 'PDF not found') {
                setError('publicStories.PDFNotFound')
            } else {
                console.log(error);
                setError('publicStories.downloadError');
            }
        }
        setLoading(false);
    }

    return (
        <section id="my-stories" ref={ref} className="story-section" data-bg="url('/images/bg_my_stories.png')" data-filter="brightness(0.5)">
            <h2 className='story-section-h2'>📖 {t('browseStories.myStories')} 📖</h2>
            <div className="story-section-component">
                {error && <p className='form-error'>{t(error)}</p>}

                {/* Loading screen */}
                {loading && <LoadingOverlay message={t('myStories.loadingStories')} />}

                {/* Display Message if No Stories */}
                {!loading && stories.length === 0 && (
                    <div className="no-stories-message">
                        <p>{t('myStories.noStories')}</p>
                    </div>
                )}

                {!loading && stories.length > 0 && (
                    <div style={{ display: 'flex' }}>
                        <div className="pagination-controls">
                            <button className='nav-button' onClick={handlePrevPage} disabled={page === 1}><i className="fa-solid fa-arrow-left nav-button-icon"></i></button>
                        </div>
                        <div className="story-grid">
                            {stories.map(story => (
                                <div className="story-card" key={story._id}>
                                {story.generatingStatus !== 'CompletelyGenerated' ? (
                                    <>
                                        <div className="story-image-container">
                                            {story.coverImage ? (<img
                                                src={story.coverImage}
                                                alt={story.title}
                                                className="story-image"
                                            />) : (
                                                <img
                                                    src="/images/default-cover.png"
                                                    alt={story.title}
                                                    className="cover-placeholder shimmer"
                                                />
                                            )}
                                            {story.generatingStatus === 'PartiallyGenerated' ? (<div className="story-generating-spinner">
                                                <p className="status-tag pulse">{t('myStories.PartiallyGeneratedTitle')}</p>
                                            </div>) : (<div className="story-generating-spinner">
                                                <div className="spinner"></div>
                                            </div>)}
                                        </div>
                                        <div className="story-title">
                                            <p className="status-tag pulse">{story.title ? story.title : t(`myStories.${story.generatingStatus}`)}</p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="story-image-container">
                                            <img
                                                src={story.coverImage}
                                                alt={story.title}
                                                className="story-image"
                                                onClick={() => handleStoryClick(story)}
                                            />
                                            {story.isPublic ? (
                                                <div className="story-feedback-icons">
                                                    <span>👍 {story.likes || 0}</span>
                                                    <span>👎 {story.dislikes || 0}</span>
                                                    <span className="feedback-btn" onClick={() => openFeedbackModal(story._id)}>
                                                        💬 {story.feedbackCount || 0}
                                                    </span>
                                                    <span className="download-btn" onClick={() => handleDownload(story._id)}>
                                                        ⏬
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className='story-feedback-icons'>
                                                    <span className='story-unpublished'>🚧{t('myStories.notPublic')}🚧</span>
                                                    <span className="download-btn" onClick={() => handleDownload(story._id)}>
                                                        ⏬
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="story-title">{story.title}</div>
                                    </>
                                )}                                
                                </div>
                            ))}
                        </div>
                        <div className="pagination-controls">
                            <button className='nav-button' onClick={handleNextPage} disabled={page >= totalPages}><i className="fa-solid fa-arrow-right nav-button-icon"></i></button>
                        </div>
                    </div>
                )}

                {selectedStory && (
                    <div className="overlay" onClick={handleCloseOverlay}>
                        <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
                            <img src={selectedStory.coverImage} alt={selectedStory.title} className="overlay-image" />
                            <h2 className="overlay-title">{selectedStory.title}</h2>
                            <p className="overlay-date">
                                {t('myStories.created')} {formatDistanceToNow(t, new Date(selectedStory.date))}
                            </p>
                            <div className="overlay-buttons">
                                <button className="overlay-btn" onClick={() => handlePlayStory(selectedStory.slug)}>
                                    <i className="fa-solid fa-book-open-reader"></i>
                                    {t('myStories.playStory')}
                                </button>
                                {!selectedStory.isPublic && (<button className="overlay-btn" onClick={() => handleMakePublic(selectedStory._id)}>
                                    <i className="fa-solid fa-universal-access"></i>
                                    {t('myStories.makePublic')}
                                </button>)}
                                <button className="overlay-btn" onClick={() => openShareModal(selectedStory)}>
                                    <i className="fa-solid fa-paper-plane"></i>
                                    {t('myStories.share')}
                                </button>
                                <button className="overlay-btn" onClick={() => handleDeleteStory(selectedStory._id)}>
                                    <i className="fa-solid fa-trash"></i>
                                    {t('myStories.deleteStory')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <FeedbackModal
                    isOpen={showFeedbackModal}
                    onClose={() => setShowFeedbackModal(false)}
                    feedbacks={feedbackData}
                    totalFeedbacks={totalFeedbacks}
                    page={feedbackPage}
                    fetchFeedbacks={fetchFeedbacks}
                    selectedStoryId={selectedStoryId}
                />

                <StoryShareModal
                    isOpen={showShareModal}
                    onClose={() => setShowShareModal(false)}
                    story={selectedStoryToShare}
                />

                <QuestionModal
                    isOpen={showDeleteConfirmationModal}
                    onClose={() => setShowDeleteConfirmationModal(false)}
                    title={t('myStories.deleteStoryTitle')}
                    message={t('myStories.deleteStoryQuestion')}
                    onConfirm={confirmDelete}
                    confirmLabel={t('myStories.deleteStory')}
                    cancelLabel={t('myStories.cancel')}
                />

                <Modal isOpen={modalMessage} onClose={() => setModalMessage(null)}>
                    <p>{t(modalMessage)}</p>
                    <div className="modal-buttons">
                        <button className="close-btn" onClick={() => setModalMessage(null)}>{t('myStories.OK')}</button>
                    </div>
                </Modal>

            </div>
        </section>
    );
};

export default MyStories;
